/**
 * 语音合成节点 - 接收文本并生成语音（Minimax）
 */
import React, { memo, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import voiceService from '../../../api/VoiceService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import VoiceSelectionModal from '../../VoiceSelectionModal';
import { TaskPricePreview } from './TaskPricePreview';
import { useTaskQuote } from './useTaskQuote';
import './TextToAudioNode.css';

export const TextToAudioNode = memo(({ id, data }) => {
  const { updateNodeData, getNode } = useReactFlow();
  const { addNodesWithEdges, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const canvasNodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  
  // 基础配置
  const [model, setModel] = useState(data.model || 'speech-2.6-hd');
  const [selectedVoiceId, setSelectedVoiceId] = useState(data.selectedVoiceId || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // 高级设置
  const [showAdvanced, setShowAdvanced] = useState(data.showAdvanced || false);
  const [speed, setSpeed] = useState(data.speed || 1.0);
  const [volume, setVolume] = useState(data.volume || 1.0);
  const [pitch, setPitch] = useState(data.pitch || 0);
  const [emotion, setEmotion] = useState(data.emotion || '');
  const [sampleRate, setSampleRate] = useState(data.sampleRate || 32000);
  const [bitrate, setBitrate] = useState(data.bitrate || 128000);
  const [format, setFormat] = useState(data.format || 'mp3');
  
  // 音色列表
  const [voiceList, setVoiceList] = useState([]);
  const [userVoices, setUserVoices] = useState([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(true);
  
  // 音色选择弹窗
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  
  // 试听音频状态
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const previewAudioRef = useRef(null);

  // 模型选项
  const models = [
    { value: 'speech-2.6-hd', label: 'HD 高清版', description: '韵律出色' },
    { value: 'speech-2.6-turbo', label: 'Turbo 快速版', description: '超低时延' },
    { value: 'speech-02-hd', label: 'HD 稳定版', description: '稳定性好' },
    { value: 'speech-02-turbo', label: 'Turbo 多语言版', description: '多语种' },
  ];

  // 情绪选项
  const emotions = [
    { value: '', label: '默认' },
    { value: 'happy', label: '开心' },
    { value: 'sad', label: '悲伤' },
    { value: 'angry', label: '愤怒' },
    { value: 'fearful', label: '恐惧' },
    { value: 'surprised', label: '惊讶' },
    { value: 'calm', label: '平静' },
    { value: 'fluent', label: '流畅' },
  ];

  // 加载音色列表
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const allVoices = await voiceService.getVoiceList();
        const userVoiceList = allVoices.filter(voice => !voice.is_public);

        setVoiceList(allVoices);
        setUserVoices(userVoiceList);
        
        // 如果没有选中音色，默认选中第一个
        if (!selectedVoiceId && allVoices.length > 0) {
          setSelectedVoiceId(allVoices[0].id);
        }
      } catch (error) {
        console.error('加载音色列表失败:', error);
        toast.error('加载音色列表失败');
      } finally {
        setIsLoadingVoices(false);
      }
    };

    loadVoices();
  }, []);

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      model,
      selectedVoiceId,
      showAdvanced,
      speed,
      volume,
      pitch,
      emotion,
      sampleRate,
      bitrate,
      format,
    });
  }, [id, model, selectedVoiceId, showAdvanced, speed, volume, pitch, emotion, 
      sampleRate, bitrate, format, updateNodeData]);

  const connectedText = useMemo(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    if (connectedEdges.length === 0) return null;
    const sourceNode = canvasNodes.find(node => node.id === connectedEdges[0].source);
    return sourceNode?.data?.prompt || null;
  }, [canvasNodes, edges, id]);

  const inputParams = useMemo(() => {
    const nextParams = {
      model,
      text: connectedText || '',
      voice_setting: {
        voice_id: selectedVoiceId,
        speed,
        vol: volume,
        pitch,
      },
      audio_setting: {
        sample_rate: sampleRate,
        bitrate,
        format,
        channel: 1,
      },
      output_format: 'url',
    };
    if (emotion) nextParams.voice_setting.emotion = emotion;
    return nextParams;
  }, [bitrate, connectedText, emotion, format, model, pitch, sampleRate, selectedVoiceId, speed, volume]);

  const taskQuote = useTaskQuote({
    enabled: Boolean(connectedText && selectedVoiceId) && !readOnly,
    apiVersion: 'v2',
    appName: 'minimax/t2a',
    taskType: 'minimax_api',
    params: inputParams,
  });

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    return true;
  }, [getNode]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleInputHandleClick = useCallback(() => {
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'textToAudio', 'prompt', 'text');
  }, [id, readOnly, createNodeAndConnect]);

  // 生成语音
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取文本
      const text = connectedText;
      if (!text) {
        throw new Error('请先连接文本输入节点');
      }
      
      if (!selectedVoiceId) {
        throw new Error('请选择音色');
      }
      
      // 2. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      console.log('语音合成参数:', inputParams);

      const confirmedQuote = await taskQuote.quoteNow();
      // 4. 创建任务
      const taskData = await voiceService.createTask('minimax/t2a', inputParams);
      
      if (taskData && taskData.task_id) {
        toast.success('语音合成任务已创建！');
        
        // 5. 创建任务节点
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'audioTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskData.task_id,
            taskType: 'text-to-audio',
            status: taskData.status || 'pending',
            inputParams: inputParams,
            chargedPoints: Number(taskData.money ?? confirmedQuote.points),
          },
        };
        
        // 6. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 7. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);
        
        setIsGenerating(false);
      } else {
        throw new Error('创建任务失败');
      }
      
    } catch (err) {
      console.error('语音合成失败:', err);
      const errorMessage = err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 获取选中的音色信息
  const currentVoice = voiceList.find(v => v.id === selectedVoiceId);

  // 试听音色
  const handlePreviewVoice = (voice, e) => {
    e.stopPropagation();
    
    if (!voice.audio_url) {
      toast.warning('该音色暂无试听音频');
      return;
    }

    // 如果正在播放同一个音色，则停止
    if (previewingVoiceId === voice.id && isPreviewPlaying) {
      previewAudioRef.current?.pause();
      setIsPreviewPlaying(false);
      setPreviewingVoiceId(null);
      return;
    }

    // 停止之前的播放
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }

    // 播放新的音频
    setPreviewingVoiceId(voice.id);
    setIsPreviewPlaying(true);
    
    if (previewAudioRef.current) {
      previewAudioRef.current.src = voice.audio_url;
      previewAudioRef.current.play().catch(error => {
        console.error('播放失败:', error);
        toast.error('播放失败');
        setIsPreviewPlaying(false);
        setPreviewingVoiceId(null);
      });
    }
  };

  // 试听音频结束
  const handlePreviewEnded = () => {
    setIsPreviewPlaying(false);
    setPreviewingVoiceId(null);
  };

  // 选择音色并关闭弹窗
  const handleSelectVoice = (voiceId) => {
    setSelectedVoiceId(voiceId);
    setShowVoiceModal(false);
    // 停止试听
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setIsPreviewPlaying(false);
    setPreviewingVoiceId(null);
  };

  return (
    <>
      {/* 试听音频播放器（隐藏） */}
      <audio
        ref={previewAudioRef}
        onEnded={handlePreviewEnded}
        style={{ display: 'none' }}
      />

      {/* 音色选择弹窗 */}
      <VoiceSelectionModal
        show={showVoiceModal}
        onHide={() => {
          setShowVoiceModal(false);
          // 停止试听
          if (previewAudioRef.current) {
            previewAudioRef.current.pause();
          }
          setIsPreviewPlaying(false);
          setPreviewingVoiceId(null);
        }}
        onSelectVoice={handleSelectVoice}
        voiceList={voiceList}
        userVoices={userVoices}
        isLoadingVoices={isLoadingVoices}
        selectedVoice={selectedVoiceId}
        previewingVoiceId={previewingVoiceId}
        isPreviewPlaying={isPreviewPlaying}
        onPreviewVoice={handlePreviewVoice}
      />

      <NodeLayout 
        id={id} 
        type="textToAudio" 
        title="语音合成（海螺）"
        showInputHandle={true}
        showOutputHandle={true}
        inputHandleId="prompt"
        inputHandleLabel="文本"
        isValidConnection={isValidConnection}
        onInputHandleClick={handleInputHandleClick}
      >
        <div className="TextToAudioNode-wrapper">
          {/* 模型选择 */}
          <div className="TextToAudioNode-field">
            <label className="TextToAudioNode-label">模型</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={isGenerating || readOnly}
              className="TextToAudioNode-select"
            >
              {models.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label} - {m.description}
                </option>
              ))}
            </select>
          </div>

          {/* 音色选择区域 */}
          <div className="TextToAudioNode-field">
            <label className="TextToAudioNode-label">音色</label>
            {isLoadingVoices ? (
              <div className="TextToAudioNode-loading-voices">加载音色中...</div>
            ) : currentVoice ? (
              <div className="TextToAudioNode-voice-selector">
                <div className="TextToAudioNode-voice-info">
                  <div className="TextToAudioNode-voice-name">
                    {currentVoice.name}
                  </div>
                  <div className="TextToAudioNode-voice-desc">
                    {currentVoice.description}
                  </div>
                </div>
                <div className="TextToAudioNode-voice-actions">
                  {currentVoice.audio_url && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewVoice(currentVoice, e);
                      }}
                      disabled={readOnly}
                      className="TextToAudioNode-voice-preview-btn"
                      title={previewingVoiceId === currentVoice.id && isPreviewPlaying ? '停止试听' : '试听'}
                    >
                      {previewingVoiceId === currentVoice.id && isPreviewPlaying ? '⏸' : '▶'}
                    </button>
                  )}
                  <button
                    onClick={() => setShowVoiceModal(true)}
                    disabled={readOnly}
                    className="TextToAudioNode-voice-change-btn"
                  >
                    更换音色
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowVoiceModal(true)}
                disabled={readOnly}
                className="TextToAudioNode-voice-select-btn"
              >
                选择音色
              </button>
            )}
          </div>

        {/* 高级设置开关 */}
        <div className="TextToAudioNode-advanced-toggle">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            disabled={readOnly}
            className="TextToAudioNode-toggle-button"
          >
            {showAdvanced ? '▼' : '▶'} 高级设置
          </button>
        </div>

        {/* 高级设置面板 */}
        {showAdvanced && (
          <div className="TextToAudioNode-advanced">
            {/* 语速 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">
                语速: {speed.toFixed(1)}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-slider"
              />
            </div>

            {/* 音量 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">
                音量: {volume.toFixed(1)}
              </label>
              <input
                type="range"
                min="0.1"
                max="10.0"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-slider"
              />
            </div>

            {/* 音调 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">
                音调: {pitch > 0 ? '+' : ''}{pitch}
              </label>
              <input
                type="range"
                min="-12"
                max="12"
                step="1"
                value={pitch}
                onChange={(e) => setPitch(parseInt(e.target.value))}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-slider"
              />
            </div>

            {/* 情绪 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">情绪</label>
              <select
                value={emotion}
                onChange={(e) => setEmotion(e.target.value)}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-select"
              >
                {emotions.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 采样率 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">采样率</label>
              <select
                value={sampleRate}
                onChange={(e) => setSampleRate(parseInt(e.target.value))}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-select"
              >
                <option value="8000">8000 Hz</option>
                <option value="16000">16000 Hz</option>
                <option value="22050">22050 Hz</option>
                <option value="24000">24000 Hz</option>
                <option value="32000">32000 Hz</option>
                <option value="44100">44100 Hz</option>
              </select>
            </div>

            {/* 比特率 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">比特率</label>
              <select
                value={bitrate}
                onChange={(e) => setBitrate(parseInt(e.target.value))}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-select"
              >
                <option value="32000">32 kbps</option>
                <option value="64000">64 kbps</option>
                <option value="128000">128 kbps</option>
                <option value="256000">256 kbps</option>
              </select>
            </div>

            {/* 格式 */}
            <div className="TextToAudioNode-field">
              <label className="TextToAudioNode-label">格式</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                disabled={isGenerating || readOnly}
                className="TextToAudioNode-select"
              >
                <option value="mp3">MP3</option>
                <option value="wav">WAV</option>
                <option value="flac">FLAC</option>
              </select>
            </div>
          </div>
        )}

        {/* 提示 */}
        <div className="TextToAudioNode-hint">
          💡 连接文本输入节点，点击生成后会自动创建任务节点
        </div>

        <TaskPricePreview quote={taskQuote} />

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly || !selectedVoiceId || (Boolean(connectedText) && taskQuote.status !== 'success')}
          className={`TextToAudioNode-generate ${
            isGenerating || readOnly || !selectedVoiceId || (Boolean(connectedText) && taskQuote.status !== 'success')
              ? 'TextToAudioNode-generate-disabled'
              : ''
          }`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="TextToAudioNode-error">
            ❌ {error}
          </div>
        )}
        </div>
      </NodeLayout>
    </>
  );
});

TextToAudioNode.displayName = 'TextToAudioNode';
